import { useEffect, useState } from 'react';

import { ehUf, municipioDaLista, municipiosDa, nomeDaUf, UFS } from '@/lib/localidades';

import { Campo } from './Campo';
import { ListaDeEscolha } from './ListaDeEscolha';
import { EtiquetaDoPreenchido, LinhaDoPreenchido } from './LinhaDoPreenchido';
import { partesDoEndereco, type CampoDoEndereco, type ValoresDoEndereco } from './resumo-do-endereco';

/**
 * O ENDEREÇO EM DUAS CAMADAS (pedido do dono, 31/10/2026: levar o desenho do
 * lançamento de abastecimento aos outros formulários).
 *
 * Eram seis campos à vista em todo cadastro com endereço (filial, obra, posto),
 * e cinco deles o CEP já preenche. Agora:
 *
 *  - à vista ficam o **CEP** e o **Número**: o que só a pessoa sabe (o
 *    provedor do CEP nunca diz o número);
 *  - o resto aparece em **etiquetas** (rua, bairro, cidade); que veio do
 *    CEP, o próprio CEP diz logo abaixo dele;
 *  - os campos de logradouro, bairro, UF e município abrem em "Ajustar
 *    endereço" (`LinhaDoPreenchido`, o componente do estoque).
 *
 * A UF e o município são o seletor do sistema, com busca: o município sai da
 * lista do IBGE da UF escolhida (são centenas por UF, e a lista nativa não
 * busca). Trocar a UF limpa o município — isso é do formulário, que guarda os
 * valores (`aoMudar('uf', …)`).
 *
 * Desenha FILHOS DA GRADE (`formulario-grade`): os campos entram nas colunas
 * de quem o usa. O CEP NÃO mora aqui: cada cadastro desenha o `CampoDeCep`
 * logo antes deste componente (é ele que preenche o resto, e o doctor, H23,
 * cobra que todo cadastro com endereço o use por extenso).
 */
export function CamposDoEndereco({
  base,
  valores,
  preenchidos,
  erros,
  aoMudar,
  aoSair,
}: {
  /** Prefixo dos `id` dos campos. */
  readonly base: string;
  readonly valores: ValoresDoEndereco;
  /** Os campos que o CEP escreveu e a pessoa ainda não tocou (decisão 12). */
  readonly preenchidos: ReadonlySet<string>;
  readonly erros: Readonly<Partial<Record<CampoDoEndereco, string | null | undefined>>>;
  readonly aoMudar: (campo: CampoDoEndereco, valor: string) => void;
  readonly aoSair: (campo: CampoDoEndereco, valor: string) => void;
}) {
  /* OS MUNICÍPIOS DA UF ESCOLHIDA, sob demanda (IBGE). São 5.571 no país;
     carregar todos cobraria de quem nunca abre um cadastro de endereço. */
  const uf = valores.uf;
  const [municipios, setMunicipios] = useState<readonly string[]>([]);
  useEffect(() => {
    if (!ehUf(uf)) {
      setMunicipios([]);
      return undefined;
    }
    let vivo = true;
    void municipiosDa(uf).then((lista) => {
      if (vivo) setMunicipios(lista);
    });
    return () => {
      vivo = false;
    };
  }, [uf]);

  /* Abre sozinho quando um campo escondido tem erro: erro que não se vê não
     se conserta. Fora isso, quem abre é a pessoa. */
  const escondidoComErro = (['logradouro', 'bairro', 'uf', 'municipio'] as const).some((c) => Boolean(erros[c]));
  const [abertoAMao, setAbertoAMao] = useState(false);
  const aberto = abertoAMao || escondidoComErro;

  const partes = partesDoEndereco(valores);
  const idDaCamada = `${base}-endereco`;
  const marca = (campo: CampoDoEndereco) => {
    const erro = erros[campo];
    return erro ? { 'aria-invalid': true as const, 'aria-describedby': `${base}-${campo}-erro` } : {};
  };

  return (
    <>
      <Campo id={`${base}-numero`} rotulo="Número" opcional erro={erros.numero ?? null}>
        <input
          id={`${base}-numero`}
          type="text"
          autoComplete="off"
          value={valores.numero}
          placeholder="120"
          className="controle w-full"
          onChange={(evento) => {
            aoMudar('numero', evento.target.value);
          }}
          onBlur={(evento) => {
            aoSair('numero', evento.target.value);
          }}
          {...marca('numero')}
        />
      </Campo>

      <LinhaDoPreenchido
        rotulo="Endereço"
        aberta={aberto}
        aoAlternar={() => {
          setAbertoAMao((atual) => !atual);
        }}
        rotuloDoBotao="endereço"
        idDoConteudo={idDaCamada}
      >
        {partes.length === 0 ? (
          <EtiquetaDoPreenchido rotulo="Rua" valor="Sem endereço" vazia />
        ) : (
          partes.map((parte) => (
            /* Sem "· pré-preenchido" em cada uma: o CEP já diz, logo acima,
               que o endereço veio dele, e a marca repetida três vezes
               empilhava as etiquetas na janela de 640 px. Os campos, abertos,
               levam a marca. */
            <EtiquetaDoPreenchido key={parte.rotulo} rotulo={parte.rotulo} valor={parte.valor} />
          ))
        )}
      </LinhaDoPreenchido>

      {aberto ? (
        /* A camada aberta em bloco rebaixado, na grade que mede a propria
           largura (`grade-do-painel`): a barra de salvar e de quem monta o
           formulario, nao deste pedaco. */
        <div id={idDaCamada} className="campo-linha grade-do-painel bloco-rebaixado p-4">
          <div className="campo-largo min-w-0">
            <Campo
              id={`${base}-logradouro`}
              rotulo="Logradouro"
              opcional
              preenchido={preenchidos.has('logradouro')}
              erro={erros.logradouro ?? null}
            >
              <input
                id={`${base}-logradouro`}
                type="text"
                autoComplete="off"
                value={valores.logradouro}
                placeholder="Rua da Bahia"
                className="controle w-full"
                onChange={(evento) => {
                  aoMudar('logradouro', evento.target.value);
                }}
                onBlur={(evento) => {
                  aoSair('logradouro', evento.target.value);
                }}
                {...marca('logradouro')}
              />
            </Campo>
          </div>

          <Campo id={`${base}-bairro`} rotulo="Bairro" opcional preenchido={preenchidos.has('bairro')} erro={erros.bairro ?? null}>
            <input
              id={`${base}-bairro`}
              type="text"
              autoComplete="off"
              value={valores.bairro}
              className="controle w-full"
              onChange={(evento) => {
                aoMudar('bairro', evento.target.value);
              }}
              onBlur={(evento) => {
                aoSair('bairro', evento.target.value);
              }}
              {...marca('bairro')}
            />
          </Campo>

          <Campo id={`${base}-uf`} rotulo="UF" opcional preenchido={preenchidos.has('uf')} erro={erros.uf ?? null}>
            <ListaDeEscolha
              id={`${base}-uf`}
              rotulo="UF"
              valor={valores.uf}
              aoMudar={(v) => {
                aoMudar('uf', v);
              }}
              aoSair={() => {
                aoSair('uf', valores.uf);
              }}
              rotuloVazio="Sem UF"
              opcoes={UFS.map((u) => ({ valor: u.sigla, rotulo: `${u.sigla} · ${u.nome}` }))}
              idDoErro={erros.uf ? `${base}-uf-erro` : undefined}
            />
          </Campo>

          <Campo
            id={`${base}-municipio`}
            rotulo="Município"
            opcional
            preenchido={preenchidos.has('municipio')}
            erro={erros.municipio ?? null}
            {...(ehUf(uf) ? {} : { ajuda: 'Escolha a UF primeiro.' })}
          >
            <ListaDeEscolha
              id={`${base}-municipio`}
              rotulo="Município"
              valor={municipioDaLista(municipios, valores.municipio)}
              aoMudar={(v) => {
                aoMudar('municipio', v);
              }}
              aoSair={() => {
                aoSair('municipio', valores.municipio);
              }}
              rotuloVazio={ehUf(uf) ? `Sem município de ${nomeDaUf(uf) ?? uf}` : 'Sem município'}
              desabilitado={!ehUf(uf)}
              opcaoUnica={false}
              opcoes={municipios.map((m) => ({ valor: m, rotulo: m }))}
              idDoErro={erros.municipio ? `${base}-municipio-erro` : undefined}
            />
          </Campo>
        </div>
      ) : null}
    </>
  );
}
